import type { ReactNode } from "react";

import { Navigate, useSearchParams } from "src/router";
import { useCurrentSurface } from "src/cep/shared/use-current-message";

/** The full surface, not just `SurfaceEditability`: predicates that also key on
 *  the message itself (channel, say) must see the same data the drawer does. */
export type GuardedSurface = ReturnType<typeof useCurrentSurface>;

/**
 * Redirects to the parent route unless `isAllowed(surface)` holds (default:
 * content is editable). `?mode=preview` bypasses only the default gate — the
 * other drawers have no read-only mode.
 */
export const DrawerRouteGuard = ({
  children,
  isAllowed,
}: {
  children: ReactNode;
  isAllowed?: (surface: GuardedSurface) => boolean;
}) => {
  const surface = useCurrentSurface();
  const [searchParams] = useSearchParams();

  const gate = isAllowed ?? ((s: GuardedSurface) => s.content.canEdit);
  const allowPreviewBypass =
    isAllowed === undefined && searchParams.get("mode") === "preview";

  if (!gate(surface) && !allowPreviewBypass) {
    return <Navigate to=".." replace />;
  }
  return <>{children}</>;
};
